// ---------------------------------------------------------------
// scheduleNoticeService.js — "Ya está tu horario" y "han cambiado
// tus turnos", en la campana de cada persona.
//
// Antes el horario cambiaba sin que nadie se enterase: había que
// entrar a mirarlo (el aviso de "consulta esta sección con
// regularidad" estaba ahí por eso). Ahora, cada vez que la dirección
// guarda el horario de alguien, se compara con lo que había:
//   · si el mes estaba vacío → "Ya está tu horario de diciembre";
//   · si cambia algún día de HOY EN ADELANTE → "Han cambiado tus
//     turnos", con qué día y cómo queda.
// Los días que ya han pasado no avisan: corregirlos es papeleo.
//
// Un aviso por persona y mes (id fijo `horario_{uid}_{mes}`) que va
// SUMANDO los días tocados mientras no lo lea: retocar diez días uno
// a uno deja un aviso con diez cambios, no diez avisos. Si los lee y
// luego hay más cambios, empieza de cero. Si un día vuelve a quedar
// como estaba, sale de la lista.
//
// Nunca bloquea el guardado: si el aviso falla, el horario ya está
// guardado y sólo se pierde el aviso.
// ---------------------------------------------------------------
import { deleteDoc, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { MONTHS, toISO } from "../utils/date";

const pad = (n) => String(n).padStart(2, "0");
const hm = (t) => String(t || "").slice(0, 5);

/** Un día del horario en palabras ("09:00–15:00 y 17:00–20:30") */
export function describirDia(info) {
  if (!info) return "libre";
  if (info.baja) return "baja";
  if (info.ownVacation) return "vacaciones";
  if (info.guardRest) return "descanso tras guardia";
  const tramos = (info.shifts || [])
    .filter((s) => s?.start && s?.end)
    .map((s) => `${hm(s.start)}–${hm(s.end)}`);
  if (!tramos.length) return "libre";
  return `${info.guard ? "guardia " : ""}${tramos.join(" y ")}`;
}

const tieneAlgo = (days) =>
  Object.values(days || {}).some(
    (d) => d?.shifts?.length || d?.ownVacation || d?.baja || d?.guardRest
  );

const fechaCorta = (iso) =>
  new Date(iso + "T12:00").toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
  });

/** Los días de hoy en adelante que cambian: { "4": { antes, ahora } } */
function diasCambiados(key, antes, despues) {
  const [year, month] = key.split("-").map(Number);
  const hoy = toISO(new Date());
  const cambios = {};
  const enMes = new Date(year, month, 0).getDate();
  for (let d = 1; d <= enMes; d++) {
    if (`${key}-${pad(d)}` < hoy) continue;
    const a = describirDia(antes?.[String(d)]);
    const b = describirDia(despues?.[String(d)]);
    if (a !== b) cambios[String(d)] = { antes: a, ahora: b };
  }
  return cambios;
}

function textoCambios(key, cambios) {
  const dias = Object.keys(cambios)
    .map(Number)
    .sort((a, b) => a - b);
  const lineas = dias
    .slice(0, 5)
    .map((d) => {
      const c = cambios[String(d)];
      return `${fechaCorta(`${key}-${pad(d)}`)}: ${c.antes} → ${c.ahora}`;
    });
  const resto = dias.length - lineas.length;
  return lineas.join(" · ") + (resto > 0 ? ` · y ${resto} ${resto === 1 ? "día" : "días"} más` : "");
}

/**
 * Avisa a una persona de que su horario de un mes es nuevo o ha cambiado.
 * @param antes   `days` que había (null si no había horario)
 * @param despues `days` que se acaban de guardar
 */
export async function avisarCambioHorario(key, uid, antes, despues) {
  try {
    // Quien toca su propio horario ya sabe lo que ha cambiado
    if (!uid || uid === auth.currentUser?.uid) return;

    const [year, month] = key.split("-").map(Number);
    // Un mes que ya terminó entero no avisa
    const finDeMes = `${key}-${pad(new Date(year, month, 0).getDate())}`;
    if (finDeMes < toISO(new Date())) return;

    const ref = doc(db, "notifications", `horario_${uid}_${key}`);
    const previo = await getDoc(ref).then((s) => (s.exists() ? s.data() : null));
    // Si ya lo leyó (o lo quitó), lo de antes está visto: se empieza de cero
    const visto = previo && (previo.readBy?.[uid] || previo.hiddenBy?.[uid]);
    const pendiente = previo && !visto ? previo : null;

    const mesNombre = MONTHS[month - 1].toLowerCase();
    const base = {
      toUid: uid,
      tipo: "horario",
      mes: key,
      link: "schedule",
      fromName: "Horario",
      createdAt: serverTimestamp(),
      readBy: {},
      hiddenBy: {},
    };

    // Publicación de un mes que estaba vacío (o aún sin leer)
    if ((!tieneAlgo(antes) || pendiente?.nuevo) && tieneAlgo(despues)) {
      await setDoc(ref, {
        ...base,
        nuevo: true,
        cambios: {},
        title: `Ya está tu horario de ${mesNombre}`,
        body: "Toca para verlo en Horario.",
      });
      return;
    }

    // Cambios: lo nuevo se suma a lo que ya llevaba sin leer. Cada día
    // conserva su "antes" original, para que diga de dónde viene.
    const cambios = { ...(pendiente?.cambios || {}) };
    for (const [d, c] of Object.entries(diasCambiados(key, antes, despues))) {
      const desde = cambios[d]?.antes ?? c.antes;
      if (desde === c.ahora) delete cambios[d];
      else cambios[d] = { antes: desde, ahora: c.ahora };
    }

    if (!Object.keys(cambios).length) {
      // Todo ha vuelto a quedar como estaba: el aviso sin leer sobra
      if (pendiente) await deleteDoc(ref);
      return;
    }

    await setDoc(ref, {
      ...base,
      nuevo: false,
      cambios,
      title: `Han cambiado tus turnos de ${mesNombre}`,
      body: textoCambios(key, cambios),
    });
  } catch (err) {
    console.warn("No se pudo dejar el aviso de cambio de horario:", err);
  }
}

/** Lo mismo para varias personas a la vez (generador, importación) */
export function avisarCambiosHorario(key, antesPorUid, despuesPorUid) {
  return Promise.all(
    Object.entries(despuesPorUid || {}).map(([uid, days]) =>
      avisarCambioHorario(key, uid, antesPorUid?.[uid] || null, days)
    )
  );
}
